import { Box, Button, Card } from '@mui/material';
import formatString from '@utils/formatString';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useRef } from 'react';

export const AnalysisLayout = ({ results, item, children }) => {
  const contentRef = useRef(null);

  const handleDownloadPdf = () => {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 32;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 2 * margin;
    let y = margin;

    // Helper for chips
    function drawChip(label, bgColor, x, y, options = {}) {
      const chipHeight = 22;
      const padding = 12;
      const radius = 4;

      // text setup
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const textWidth = pdf.getTextWidth(label);
      const width = textWidth + padding * 2;

      // colors
      const textColor = options.color || '#FFFFFF';
      const strokeColor = options.outlineColor || bgColor;

      if (options.outline) {
        // draw only outline
        pdf.setDrawColor(...hexToRgb(strokeColor));
        pdf.setLineWidth(1);
        pdf.roundedRect(x, y, width, chipHeight, radius, radius, 'S');
      } else {
        // filled chip
        pdf.setFillColor(...hexToRgb(bgColor));
        pdf.roundedRect(x, y, width, chipHeight, radius, radius, 'F');
      }

      // draw the label
      pdf.setTextColor(...hexToRgb(textColor));
      pdf.text(label, x + padding, y + 15);

      // reset draw/text color
      pdf.setDrawColor(0, 0, 0);
      pdf.setTextColor(0, 0, 0);

      // return total advance (width + gap)
      return width + 8;
    }
    const intensityColor = {
      high: '#B42318',
      moderate: '#F79009',
      low: '#2E90FA',
      success: '#66bb6a',
    };

    // Helper to convert hex string to [r, g, b]
    function hexToRgb(hex) {
      const clean = hex.replace('#', '');
      const bigint = parseInt(clean, 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    }

    // Start::Header (Card style)
    pdf.setFillColor(245, 245, 245);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(33, 33, 33);
    pdf.text(`${item.name}`, margin, y, 0.5);
    y += 5;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(70, 70, 70);
    pdf.text(
      `Date: ${new Date(item.created_at?.$date || item.created_at).toLocaleString()}`,
      margin,
      y + 20,
    );
    y = y + 35;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, usableWidth + margin, y);
    y += 20;

    // End::Header (Card style)

    // Start::Metadata Section
    // Layout constants
    const cardX = margin;
    const cardY = y;
    const cardW = usableWidth;
    const headerH = 32;
    const padding = 12;
    const rowGap = 38;
    const cols = 2;
    const colW = (cardW - padding * 2) / cols;

    // Define your items
    const metaItems = [
      ['Analysis ID', item._id?.$oid || item.analysis_item_id || 'N/A'],
      ['Study ID', item.study_id || 'N/A'],
      ['Created', new Date(item.created_at?.$date || item.created_at).toLocaleString()],
      ['Lens Type', formatString(item.analysis_lens_type || 'N/A')],
      ['Transcripts', `${results?.transcripts_analyzed ?? 0}`],
    ];
    const rows = Math.ceil(metaItems.length / cols);
    const cardH = headerH + padding + rows * rowGap + padding;

    // Draw card border
    pdf.setDrawColor(200, 200, 200).setLineWidth(0.5).rect(cardX, cardY, cardW, cardH);

    // Header background
    pdf.setFillColor(245, 245, 245).rect(cardX, cardY, cardW, headerH, 'F');

    // Header text with tightened char spacing
    pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(33, 33, 33);
    pdf.text('Analysis Metadata', cardX + 10, cardY + headerH - 10);

    // Start drawing rows
    let bodyY = cardY + headerH + padding;
    metaItems.forEach(([label, value], i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = cardX + padding + col * colW;
      let yRow = bodyY + row * rowGap;

      // Label (subtitle2 style)
      pdf.setFont('helvetica', 'bold').setFontSize(10).setTextColor(33, 33, 33);
      pdf.text(label, x, yRow);
      yRow += 4;

      // Value
      if (label === 'Lens Type') {
        // render as chip
        const chipY = yRow + 3; // adjust so chip aligns nicely under the label
        drawChip(value, '#7d5ae8', x, chipY, { color: '#ffffff' });
      } else {
        // normal text wrap
        pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(70, 70, 70);
        const lines = pdf.splitTextToSize(value, colW - padding);
        lines.forEach((ln, li) => {
          pdf.text(ln, x, yRow + 12 + li * 12);
        });
      }
    });

    // Advance y past the card + bottom padding
    y = cardY + cardH + padding;
    // End::Metadata Section

    // — just after your metadata block —
    if (results.summary) {
      y += 24;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(33, 33, 33);
      pdf.text('Overall Summary', margin, y);
      y += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(70, 70, 70); // #333333 for better readability

      // Split summary into paragraphs (assuming double newline separates paragraphs)
      const paragraphs = results.summary.split('\n\n');
      const baseLineHeight = 16; // Base line height for text
      const paragraphSpacing = 8; // Additional space between paragraphs

      paragraphs.forEach((paragraph, pIndex) => {
        if (pIndex > 0) {
          y += paragraphSpacing; // Add extra space between paragraphs
        }

        const lines = pdf.splitTextToSize(paragraph.trim(), 500 /* wrap width in pts */);
        lines.forEach((line, i) => {
          // Check if we need a new page
          if (y > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + i * baseLineHeight);
        });
        y += lines.length * baseLineHeight;
      });
      // Define all chips with their labels and values
      const chips = [
        results?.transcripts_analyzed !== undefined && {
          label: `Transcripts Analyzed: ${results.transcripts_analyzed || 0}`,
          value: results.transcripts_analyzed,
        },
        results?.total_unique_themes !== undefined && {
          label: `Total Unique Themes: ${results.total_unique_themes || 0}`,
          value: results.total_unique_themes,
        },
        results?.total_unique_jobs !== undefined && {
          label: `${results.total_unique_jobs || 0} Total Unique Jobs`,
          value: results.total_unique_jobs,
        },
        results?.total_signals !== undefined && {
          label: `${results.total_signals || 0} Total Signals`,
          value: results.total_signals,
        },
        results?.total_unique_concepts !== undefined && {
          label: `${results.total_unique_concepts || 0} Total Concepts`,
          value: results.total_unique_concepts,
        },
        results?.saturation_status_overall !== undefined && {
          label: `Saturation: ${results.saturation_status_overall || 'N/A'}`,
          value: results.saturation_status_overall,
        },
        results?.total_aligned_elements !== undefined && {
          label: `Aligned Elements: ${results.total_aligned_elements || 0}`,
          value: results.total_aligned_elements,
        },
        results?.total_misaligned_elements !== undefined && {
          label: `Misaligned Elements: ${results.total_misaligned_elements || 0}`,
          value: results.total_misaligned_elements,
        },
        results?.total_emergent_elements !== undefined && {
          label: `Emergent Elements: ${results.total_emergent_elements || 0}`,
          value: results.total_emergent_elements,
        },
        results?.total_resonant_messages !== undefined && {
          label: `Resonant Messages: ${results.total_resonant_messages || 0}`,
          value: results.total_resonant_messages,
        },
        results?.total_missed_messages !== undefined && {
          label: `Missed Messages: ${results.total_missed_messages || 0}`,
          value: results.total_missed_messages,
        },
        results?.total_unique_outcomes !== undefined && {
          label: `Total Unique Outcomes: ${results.total_unique_outcomes || 0}`,
          value: results.total_unique_outcomes,
        },
        results?.total_unique_barriers !== undefined && {
          label: `Total Unique Barriers: ${results.total_unique_barriers || 0}`,
          value: results.total_unique_barriers,
        },
      ].filter(Boolean); // Remove any falsy values

      // Draw chips with automatic line wrapping
      const chipPadding = 10; // Horizontal padding between chips
      const chipLineHeight = 30; // Vertical space for each line of chips
      let currentX = margin;
      let currentY = y;
      const maxWidth = pdf.internal.pageSize.width - 2 * margin;

      chips.forEach((chip) => {
        // Measure the width of the chip
        const chipWidth = pdf.getStringUnitWidth(chip.label) * 3.5 + 20; // Approximate width with padding

        // Check if we need to move to a new line
        if (currentX + chipWidth > maxWidth && currentX > margin) {
          currentX = margin;
          currentY += chipLineHeight;
        }

        // Draw the chip
        const drawnWidth = drawChip(chip.label, '#66bb6a', currentX, currentY, {
          color: intensityColor.low,
          outlineColor: intensityColor.low,
          outline: true,
        });

        // Update position for next chip
        currentX += drawnWidth + chipPadding;
      });

      // Update y position for the next element
      y = currentY + chipLineHeight;

      // Move y position down for next element
      y += 16; // Adjust this value as needed for proper spacing
    }

    // Start::Top Qualities (wrapped chips with spacing)
    if (results?.top_qualities) {
      y += 20;
      // Header
      pdf.setFontSize(12).setFont('helvetica', 'bold').setTextColor(0, 0, 0);
      pdf.text('Top Qualities', margin, y);
      y += 16;

      // Chip layout settings
      const chipHeight = 22;
      const paddingH = 12; // horizontal padding inside chip
      const interChipSpacing = 8; // space between chips
      const maxX = margin + usableWidth;
      let x = margin;

      // Draw each chip, wrapping to next line if it would overflow
      results.top_qualities.forEach(({ quality }) => {
        // measure text width + padding*2 + a little extra spacing
        const textW = pdf.getTextWidth(quality);
        const chipW = textW + paddingH * 2 + interChipSpacing;

        // wrap
        if (x + chipW > maxX) {
          x = margin;
          y += chipHeight + interChipSpacing;
        }

        // draw and advance x
        x += drawChip(formatString(quality), '#66bb6a', x, y, {
          color: '#7d5ae8',
          outlineColor: '#7d5ae8',
          outline: true,
        });
      });

      // move y down past the last row of chips
      y += chipHeight;
    }
    // Start::Top Frictions (wrapped chips with spacing)
    if (results?.top_friction_points) {
      y += 20;
      // Header
      pdf.setFontSize(12).setFont('helvetica', 'bold').setTextColor(0, 0, 0);
      pdf.text('Top Friction Points', margin, y);
      y += 16;

      // Chip layout settings
      const chipHeight = 22;
      const paddingH = 12; // horizontal padding inside chip
      const interChipSpacing = 8; // space between chips
      const maxX = margin + usableWidth;
      let x = margin;

      // Draw each chip, wrapping to next line if it would overflow
      results.top_friction_points.forEach(({ friction_point }) => {
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin + 10;
        }
        // measure text width + padding*2 + a little extra spacing
        const textW = pdf.getTextWidth(friction_point);
        const chipW = textW + paddingH * 2 + interChipSpacing;

        // wrap
        if (x + chipW > maxX) {
          x = margin;
          y += chipHeight + interChipSpacing;
        }

        // draw and advance x
        x += drawChip(friction_point, '#7d5ae8', x, y, {
          color: intensityColor.high,
          outlineColor: intensityColor.high,
          outline: true,
        });
      });

      // move y down past the last row of chips
      y += chipHeight;
    }

    // --- Start::Signal Distribution (5-column layout, no extra letter spacing) ---
    if (results?.signal_distribution) {
      // top margin
      y += 24;

      // Header
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0).setCharSpace(0); // ensure default spacing
      pdf.text('Signal Distribution', margin, y);
      y += 30;

      // Prepare data
      const dist = results.signal_distribution;
      const entries = Object.entries(dist); // five entries
      const cols = entries.length; // 5 columns
      const colW = usableWidth / cols;

      // Font sizing
      const valFontSize = 16;
      const capFontSize = 10;
      const valLineHeight = valFontSize + 2;
      const capLineHeight = capFontSize + 2;
      const rowGap = 12;

      // Draw each cell in one row of five
      entries.forEach(([key, value], idx) => {
        const cellX = margin + idx * colW;
        const cellY = y;

        // Value (centered)
        pdf.setFont('helvetica', 'bold').setFontSize(valFontSize).setTextColor(33, 33, 33);
        pdf.text(String(value), cellX + colW / 2, cellY, { align: 'center' });

        // Caption (capitalized, centered below)
        pdf.setFont('helvetica', 'normal').setFontSize(capFontSize).setTextColor(70, 70, 70);
        pdf.text(
          key.charAt(0).toUpperCase() + key.slice(1),
          cellX + colW / 2,
          cellY + valLineHeight,
          { align: 'center' },
        );
      });

      // Advance Y past this grid
      y += valLineHeight + capLineHeight + rowGap;
    }
    // --- End::Signal Distribution ---

    if (results?.score_distribution) {
      const { high, moderate, low } = results.score_distribution;

      // space before this section
      y += 24;

      // Section header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Score Distribution', margin, y);
      y += 20;

      // draw each chip side‐by‐side
      const chipColors = {
        'High (>=80)': '#66bb6a',
        'Moderate (60–79)': '#ffa726',
        'Low (<60)': '#ef5350',
      };
      let x = margin;
      [
        ['High (>=80)', high],
        ['Moderate (60–79)', moderate],
        ['Low (<60)', low],
      ].forEach(([label, count]) => {
        const text = `${label}: ${count}`;
        const chipWidth = drawChip(text, chipColors[label], x, y, {
          color: '#ffffff',
        });
        x += chipWidth; // advance x by chip width + your internal 8px spacing
      });

      // move y down after chips
      y += 36; // or whatever spacing works for you, e.g. y += 36;
    }

    // Start::Barrier Type Distribution
    if (results?.barrier_type_distribution) {
      const dist = results.barrier_type_distribution;
      const total = Object.values(dist).reduce((sum, v) => sum + v, 0);

      // Section header
      y += 24;
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('Barrier Type Distribution', margin, y);
      y += 16;

      const barHeight = 8;
      const lineGap = 14; // space after each line

      Object.entries(dist).forEach(([type, count]) => {
        // Capitalize type and compute percentage
        const label = `${type.charAt(0).toUpperCase() + type.slice(1)}:`;
        const percent = total ? (count / total) * 100 : 0;
        const pctText = `${percent.toFixed(0)}% (${count})`;

        // Measure widths
        pdf.setFont('helvetica', 'normal').setFontSize(10);
        const labelW = pdf.getTextWidth(label + ' ');
        const textW = pdf.getTextWidth(pctText);
        // Bar fits in the remaining space
        const barFullW = usableWidth - labelW - textW - 12; // 6pt padding each side

        // Draw label
        pdf.text(label, margin, y + barHeight);

        // Draw background bar
        const barX = margin + labelW + 6;
        pdf.setFillColor(230, 230, 230);
        pdf.rect(barX, y, barFullW, barHeight, 'F');

        // Draw filled portion
        pdf.setFillColor(125, 94, 232);
        pdf.rect(barX, y, barFullW * (percent / 100), barHeight, 'F');

        // Draw percentage + count
        pdf.setFontSize(10).setTextColor(0, 0, 0);
        pdf.text(pctText, barX + barFullW + 6, y + barHeight);

        // Move down for next line & handle page breaks
        y += barHeight + lineGap;
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
      });
    }
    // Start::Reaction Type Distribution
    if (results?.reaction_type_distribution) {
      const dist = results.reaction_type_distribution;
      const total = Object.values(dist).reduce((sum, v) => sum + v, 0);

      // Section header
      y += 24;
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('Reaction Type Distribution', margin, y);
      y += 16;

      const barHeight = 8;
      const lineGap = 14; // space after each line

      Object.entries(dist).forEach(([type, count]) => {
        // Capitalize type and compute percentage
        const label = `${type.charAt(0).toUpperCase() + type.slice(1)}:`;
        const percent = total ? (count / total) * 100 : 0;
        const pctText = `${percent.toFixed(0)}% (${count})`;

        // Measure widths
        pdf.setFont('helvetica', 'normal').setFontSize(10);
        const labelW = pdf.getTextWidth(label + ' ');
        const textW = pdf.getTextWidth(pctText);
        // Bar fits in the remaining space
        const barFullW = usableWidth - labelW - textW - 12; // 6pt padding each side

        // Draw label
        pdf.text(label, margin, y + barHeight);

        // Draw background bar
        const barX = margin + labelW + 6;
        pdf.setFillColor(230, 230, 230);
        pdf.rect(barX, y, barFullW, barHeight, 'F');

        // Draw filled portion
        pdf.setFillColor(125, 94, 232);
        pdf.rect(barX, y, barFullW * (percent / 100), barHeight, 'F');

        // Draw percentage + count
        pdf.setFontSize(10).setTextColor(0, 0, 0);
        pdf.text(pctText, barX + barFullW + 6, y + barHeight);

        // Move down for next line & handle page breaks
        y += barHeight + lineGap;
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
      });
    }
    // Start::Aggregate Sentiment Distribution
    if (results?.aggregate_sentiment_distribution) {
      const dist = results.aggregate_sentiment_distribution;
      const total = Object.values(dist).reduce((sum, v) => sum + v, 0);

      // Section header
      y += 24;
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('Aggregate Sentiment Distribution', margin, y);
      y += 16;

      const barHeight = 8;
      const lineGap = 14; // space after each line

      Object.entries(dist).forEach(([type, count]) => {
        // Capitalize type and compute percentage
        const label = `${type.charAt(0).toUpperCase() + type.slice(1)}:`;
        const percent = total ? (count / total) * 100 : 0;
        const pctText = `${percent.toFixed(0)}% (${count})`;

        // Measure widths
        pdf.setFont('helvetica', 'normal').setFontSize(10);
        const labelW = pdf.getTextWidth(label + ' ');
        const textW = pdf.getTextWidth(pctText);
        // Bar fits in the remaining space
        const barFullW = usableWidth - labelW - textW - 12; // 6pt padding each side

        // Draw label
        pdf.text(label, margin, y + barHeight);

        // Draw background bar
        const barX = margin + labelW + 6;
        pdf.setFillColor(230, 230, 230);
        pdf.rect(barX, y, barFullW, barHeight, 'F');

        // Draw filled portion
        {
          type === 'positive'
            ? pdf.setFillColor(intensityColor.success)
            : type === 'negative'
              ? pdf.setFillColor(intensityColor.high)
              : pdf.setFillColor(intensityColor.low);
        }
        pdf.rect(barX, y, barFullW * (percent / 100), barHeight, 'F');

        // Draw percentage + count
        pdf.setFontSize(10).setTextColor(70, 70, 70);
        pdf.text(pctText, barX + barFullW + 6, y + barHeight);

        // Move down for next line & handle page breaks
        y += barHeight + lineGap;
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
      });
    }
    // Start::Theme Prevalence
    if (results?.theme_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Theme Prevalence', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.theme_prevalence?.forEach((themeData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${themeData.theme}`, margin, y + 8);
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(10)
          .setTextColor(80, 80, 80)
          .text(
            `# Prevalence: ${themeData.prevalence}`,
            pdf.internal.pageSize.width -
              margin -
              pdf.getTextWidth(`Prevalence: ${themeData.prevalence}`) -
              2,
            y + 8,
          );
        y += 20;

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 10;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.text('Representative Meaning', margin, y);
        y += 16;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        const lines = pdf.splitTextToSize(themeData.representative_meaning, 500);
        const lineHeight = 16;
        lines.forEach((line, i) => {
          const newY = y + i * lineHeight;
          if (newY > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + i * lineHeight);
        });
        y += lines.length * lineHeight;
        y += 10;

        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Common Qualities', margin, y);
        y += 8;
        // Start::Common Qualities (wrapped chips with spacing)
        if (themeData.common_qualities) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin;
          }

          // Layout settings
          const chipHeight = 22;
          const paddingH = 12; // horizontal padding inside each chip
          const interChipSpacing = 8; // space between chips
          const maxX = margin + usableWidth;
          let x = margin;

          // Draw each quality as a chip, wrapping when needed
          themeData.common_qualities.forEach((quality) => {
            if (y > pdf.internal.pageSize.height - 100) {
              pdf.addPage();
              y = margin;
            }
            // measure how wide this chip will be
            const textW = pdf.getTextWidth(quality);
            const chipW = textW + paddingH * 2 + interChipSpacing;

            // if it would overflow, wrap to next line
            if (x + chipW > maxX) {
              x = margin;
              y += chipHeight + interChipSpacing;
            }

            // draw the chip and advance x
            x += drawChip(formatString(quality), '#66bb6a', x, y, {
              color: '#7d5ae8',
              outlineColor: '#7d5ae8',
              outline: true,
            });
          });

          // move y past the last row of chips
          y += chipHeight + 24;
        }
        // End::Common Qualities

        // Draw representative quotes
        if (themeData.representative_quotes?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          themeData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    if (results?.themes) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(125, 84, 136);
      pdf.text('Theme Insights', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.themes?.forEach((themeData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${themeData.theme}`, margin, y + 8);
        y += 20;

        // Check page space before summary section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Summary
        y += 10;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.text('Summary', margin, y);
        y += 16;

        // Draw summary text
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        const summaryLines = pdf.splitTextToSize(themeData.summary, 500);
        const lineHeight = 16;
        summaryLines.forEach((line, i) => {
          const newY = y + i * lineHeight;
          if (newY > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + i * lineHeight);
        });
        y += summaryLines.length * lineHeight;
        y += 20;

        // Draw representative quotes
        if (themeData.representative_quotes?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin;
          }

          // Section title
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);
          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          themeData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const quoteLineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - quoteLineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += quoteLineHeight;
            });

            y += quoteLineHeight; // Add space after each quote

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * quoteLineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }

    // Start::Arc Prevalence
    if (results?.arc_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Narrative Arc Breakdown', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.arc_prevalence?.forEach((arcData) => {
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${arcData.arc_label}`, margin, y + 8);
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(10)
          .setTextColor(80, 80, 80)
          .text(
            `# Prevalence: ${arcData.prevalence}`,
            pdf.internal.pageSize.width -
              margin -
              pdf.getTextWidth(`Prevalence: ${arcData.prevalence}`) -
              2,
            y + 8,
          );
        y += 20;

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 10;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Common Emotional Journey', margin, y);
        y += 8;
        // Draw representative meaning
        if (arcData.common_emotional_journey) {
          if (y > pdf.internal.pageSize.height - 50) {
            pdf.addPage();
            y = margin;
          }
          // set list font
          y += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);
          const lineHeight = 14;
          arcData.common_emotional_journey.forEach((journey) => {
            // draw a bullet and the text
            pdf.text(`${journey}`, margin, y);
            y += lineHeight;

            // page-break check
            if (y > pdf.internal.pageSize.height - 40) {
              pdf.addPage();
              y = margin;
            }
          });
        }

        // Draw representative quotes
        if (arcData.representative_quotes?.length) {
          y += 24; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          arcData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Barrier Prevalence
    if (results?.barrier_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        'Prevelent Barriers' + ' (' + `${results?.barrier_prevalence?.length}` + ')',
        margin,
        y,
      );
      y += 20;

      // Draw theme cards with background and separation
      results?.barrier_prevalence?.forEach((arcData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type Chip + Prevalence Header ---
        {
          const jobText = arcData.barrier;
          const jobTypeText = '';
          const prevalenceText = `# Prevalence: ${arcData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw representative quotes
        if (arcData.representative_quotes?.length) {
          if (y > pdf.internal.pageSize.height - 50) {
            pdf.addPage();
            y = margin + 10;
          }

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          arcData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Outcome Prevalence
    if (results?.outcome_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        'Prevelent Outcomes' + ' (' + `  ${results?.outcome_prevalence?.length}` + ')',
        margin,
        y,
      );
      y += 20;

      // Draw theme cards with background and separation
      results?.outcome_prevalence?.forEach((arcData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type Chip + Prevalence Header ---
        {
          const jobText = arcData.outcome;
          const jobTypeText = '';
          const prevalenceText = `# Prevalence: ${arcData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Common Barriers', margin, y);
        y += 8;
        // Draw common discursive strategies
        if (arcData.common_barriers) {
          // space after the header
          y += 8;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);

          const lineHeight = 14;
          arcData.common_barriers.forEach((barrier) => {
            // page‐break check
            if (y > pdf.internal.pageSize.height - 40) {
              pdf.addPage();
              y = margin;
            }

            // draw bullet + text
            pdf.text(`• ${barrier}`, margin + 8, y);
            y += lineHeight;
          });
        }

        // Draw representative quotes
        if (arcData.representative_quotes?.length) {
          y += 24; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          arcData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::frame Prevalence
    if (results?.frame_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Frame Prevalence', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.frame_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${frameData.label}`, margin, y + 8);
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(10)
          .setTextColor(80, 80, 80)
          .text(
            `# Prevalence: ${frameData.prevalence}`,
            pdf.internal.pageSize.width -
              margin -
              pdf.getTextWidth(`Prevalence: ${frameData.prevalence}`) -
              2,
            y + 8,
          );
        y += 20;

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 8;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        const lines = pdf.splitTextToSize(frameData?.description_summary, 500);
        const lineHeight = 16;
        lines.forEach((line, i) => {
          const newY = y + i * lineHeight;
          if (newY > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + i * lineHeight);
        });
        y += lines.length * lineHeight;
        y += 8;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Common Discursive Strategies', margin, y);
        y += 8;
        // Draw common discursive strategies
        if (frameData.common_discursive_strategies) {
          // space after the header
          y += 8;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);

          const lineHeight = 14;
          frameData.common_discursive_strategies.forEach((strat) => {
            // page‐break check
            if (y > pdf.internal.pageSize.height - 40) {
              pdf.addPage();
              y = margin;
            }

            // draw bullet + text
            pdf.text(`• ${strat}`, margin + 8, y);
            y += lineHeight;
          });
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          y += 24; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Data Quality Notes
    if (Array.isArray(results?.data_quality_notes)) {
      // Add quality notes
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('Data Quality Notes:', margin, y);
      y += 8;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(70, 70, 70);
      results?.data_quality_notes?.forEach((quote) => {
        // Check page space before starting a new quote
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin + 10;
        }

        // Add quote mark
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(70, 70, 70);

        // Draw quote text
        const quoteLines = pdf.splitTextToSize(
          quote,
          pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
        );

        const lineHeight = 12;
        const quoteTop = y;

        // Draw each line of the quote
        quoteLines.forEach((line) => {
          if (y > pdf.internal.pageSize.height - lineHeight * 2) {
            pdf.addPage();
            y = margin + 10;
          }
          pdf.text(line, margin + 10, y + 10);
          y += lineHeight;
        });

        y += lineHeight; // Add space after each quotes

        // Adjust vertical position after the quote
        y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
      });
      y += 8;
    }
    // Start::Top Discursive Strategies
    if (results?.top_discursive_strategies) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }
      y += 8;
      // Section header
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('Top Discursive Strategies', margin, y);
      y += 8;

      // Build the head and body arrays
      const head = [['Strategy', 'Frequency']];
      const body = results.top_discursive_strategies.map((row) => [row.strategy, row.frequency]);

      // Render table
      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin },
        head: head,
        body: body,
        styles: {
          cellPadding: 2,
          fontSize: 10,
          overflow: 'linebreak',
          halign: 'left',
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        bodyStyles: {
          lineWidth: 0.5,
        },
      });

      // Move y below the table
      y = pdf.autoTable.previous.finalY + 8;
    }
    // Start::Dimensions Summary
    if (results?.dimensions_summary) {
      if (y > pdf.internal.pageSize.height - 24) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('Dimensions Analysis', margin, y);
      y += 24;

      results.dimensions_summary.forEach((dimension) => {
        if (y > pdf.internal.pageSize.height - 24) {
          pdf.addPage();
          y = margin;
        }
        const lineHeight = 18;

        const title = dimension.dimension;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(60, 60, 60);
        pdf.text(title, margin, y);

        // advance y by the chip height + padding
        y += 22 + 8; // 22 = chip height, 8px spacing

        if (dimension?.predominant_themes) {
          if (y > pdf.internal.pageSize.height - lineHeight * 2) {
            pdf.addPage();
            y = margin;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`Predominant Themes in ${dimension.dimension}`, margin, y);
          y += lineHeight;
          y += 2;

          dimension.predominant_themes.forEach((theme, j) => {
            if (y > pdf.internal.pageSize.height - lineHeight * 2) {
              pdf.addPage();
              y = margin;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(`${j + 1}. ${theme}`, margin, y);
            y += lineHeight;
          });

          y += lineHeight; // Add space between sections
        }
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }
        if (dimension?.common_gaps) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`Common Gaps in ${dimension.dimension}`, margin, y);
          y += lineHeight;
          y += 2;
          dimension.common_gaps.forEach((gap, j) => {
            if (y > pdf.internal.pageSize.height - lineHeight * 2) {
              pdf.addPage();
              y = margin;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(`${j + 1}. ${gap}`, margin, y);
            y += lineHeight;
          });

          y += lineHeight; // Add space between sections
        }

        if (dimension?.recommended_actions) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`Recommended Actions for ${dimension.dimension}`, margin, y);
          y += lineHeight;
          y += 2;
          dimension.recommended_actions.forEach((action, j) => {
            if (y > pdf.internal.pageSize.height - lineHeight * 2) {
              pdf.addPage();
              y = margin;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(`${j + 1}. ${action}`, margin, y);
            y += lineHeight;
          });

          y += lineHeight; // Add space between sections
        }

        y += 16;
      });
    }
    // Start::High Leverage Opportunities
    if (results?.high_leverage_opportunities) {
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      // heading
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('High Leverage Opportunities', margin, y);
      y += 24;

      // list items
      pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(51, 51, 51);
      const lineHeight = 18;
      results.high_leverage_opportunities.forEach((opp, i) => {
        // page-break guard
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
        // bullet + text
        pdf.text(`${i + 1}. ${opp}`, margin, y);
        y += lineHeight;
      });

      // extra padding before next section
      y += 8;
    }
    // Start::underrepresented_perspectives
    if (results?.underrepresented_perspectives) {
      // section spacing
      y += 24;

      // heading
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('Underrepresented Perspectives', margin, y);
      y += 16;

      // list items
      pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(51, 51, 51);
      const lineHeight = 14;
      results.underrepresented_perspectives.forEach((opp) => {
        // page-break guard
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
        // bullet + text
        pdf.text(`${opp}`, margin, y);
        y += lineHeight;
      });

      // extra padding before next section
      y += 8;
    }
    // Start::Top Pain Points (properly centered circles)
    if (results?.top_pain_points) {
      y += 24;
      // Heading
      pdf.setFont('helvetica', 'bold').setFontSize(14).setTextColor(0, 0, 0);
      pdf.text('Top Pain Points', margin, y);
      y += 16;

      const circleRadius = 7;
      const textIndent = circleRadius * 2 + 8; // space for circle + padding
      const wrapWidth = usableWidth - textIndent; // max width for text
      const lineHeight = 14;
      const itemSpacing = 12;

      results.underrepresented_perspectives?.forEach(({ pain_point }) => {
        // Page-break guard
        if (y > pdf.internal.pageSize.height - 60) {
          pdf.addPage();
          y = margin;
        }

        // Split text into lines
        const lines = pdf
          .setFont('helvetica', 'normal')
          .setFontSize(10)
          .setTextColor(51, 51, 51)
          .splitTextToSize(pain_point, wrapWidth);

        const blockHeight = lines.length * lineHeight;

        // Draw each line of text
        pdf.setFont('helvetica', 'normal').setFontSize(10).setTextColor(51, 51, 51);
        lines.forEach((line, i) => {
          pdf.text(line, margin + textIndent, y + i * lineHeight);
        });

        // Advance y by the full block height plus spacing
        y += blockHeight + itemSpacing;
      });

      y += 8;
    }
    // Start::Stage Prevalence
    if (results?.stage_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Stage Prevalence' + ' (' + results?.stage_prevalence?.length + ')', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.stage_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        {
          const jobText = frameData.stage;
          const jobTypeText = frameData.dominant_severity;
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 4) Draw the jobType as a chip, vertically centered
          // drawChip places text at y + 15, so supply chipY = statsY - 15
          const chipX = pageW - margin - statsTotalW;
          const chipY = statsY - 15;
          drawChip(jobTypeText, '#66bb6a', chipX, chipY, { color: '#ffffff' });

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 8;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        const lines = pdf.splitTextToSize(frameData?.common_pain_points, 500);
        const lineHeight = 16;
        lines.forEach((line, i) => {
          const newY = y + i * lineHeight;
          if (newY > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + i * lineHeight);
        });
        y += lines.length * lineHeight;
        y += 8;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        pdf.text(
          'Common Discursive Strategies' +
            ' (' +
            frameData?.common_discursive_strategies?.length +
            ')',
          margin,
          y,
        );
        y += 8;
        // Draw common discursive strategies
        if (frameData.common_underlying_causes) {
          // space after the header
          y += 8;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);

          const lineHeight = 14;
          frameData.common_underlying_causes.forEach((strat) => {
            // page‐break check
            if (y > pdf.internal.pageSize.height - 40) {
              pdf.addPage();
              y = margin;
            }

            // draw bullet + text
            pdf.text(`• ${strat}`, margin + 8, y);
            y += lineHeight;
          });
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          y += 24; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Message Prevalence
    if (results?.message_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Message Prevalence' + ' (' + results?.message_prevalence?.length + ')', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.message_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title

        {
          const jobText = frameData.message;
          const jobTypeText = frameData.reaction_type;
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 4) Draw the jobType as a chip, vertically centered
          // drawChip places text at y + 15, so supply chipY = statsY - 15
          const chipX = pageW - margin - statsTotalW;
          const chipY = statsY - 15;
          drawChip(formatString(jobTypeText), '#66bb6a', chipX, chipY, {
            color: '#66bb6a',
            outlineColor: '#66bb6a',
            outline: true,
          });

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 8;

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Top Emotion Hotspots
    if (results?.top_emotion_hotspots) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        'Top Emotion Hotspots' + ' (' + results?.top_emotion_hotspots?.length + ')',
        margin,
        y,
      );
      y += 20;

      // Draw theme cards with background and separation
      results?.top_emotion_hotspots?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type Chip + Prevalence Header ---
        {
          const emotionText = frameData.emotion;
          const intensityText = frameData.intensity;
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(intensityText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(33, 33, 33);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(emotionText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 4) Draw the jobType as a chip, vertically centered
          // drawChip places text at y + 15, so supply chipY = statsY - 15
          const chipX = pageW - margin - statsTotalW;
          const chipY = statsY - 15;

          drawChip(formatString(intensityText), intensityColor[intensityText], chipX, chipY, {
            color: intensityColor[intensityText],
            outlineColor: intensityColor[intensityText],
            outline: true,
          });

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 8;

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(33, 33, 33);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(33, 33, 33);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Common Emotional Arcs
    if (results?.common_emotional_arcs) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        'Common Emotional Arcs' + ' (' + results?.common_emotional_arcs?.length + ')',
        margin,
        y,
      );
      y += 20;

      // Draw theme cards with background and separation
      results?.common_emotional_arcs?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${frameData.label}`, margin, y + 8);
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(10)
          .setTextColor(80, 80, 80)
          .text(
            `# Prevalence: ${frameData.prevalence}`,
            pdf.internal.pageSize.width -
              margin -
              pdf.getTextWidth(`Prevalence: ${frameData.prevalence}`) -
              2,
            y + 8,
          );

        y += 20;

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Title for Representative Meaning
        y += 8;

        // Draw trigger theme quotes
        if (frameData?.pattern?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Emotional Pattern', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.pattern.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('-', margin, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
        // Draw trigger theme quotes
        if (frameData.trigger_themes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Trigger Themes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.trigger_themes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('-', margin, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Element Prevalence
    if (results?.element_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Element Prevalence' + ' (' + results?.element_prevalence?.length + ')', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.element_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // --- Wrapped Element + Classification Chip + Prevalence Header ---
        {
          const jobText = frameData.element;
          const jobTypeText = frameData.classification;
          const prevalenceText = `${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 4) Draw the jobType as a chip, vertically centered
          // drawChip places text at y + 15, so supply chipY = statsY - 15
          const chipX = pageW - margin - statsTotalW;
          const chipY = statsY - 15;
          drawChip(
            formatString(jobTypeText + ' ' + '(' + prevalenceText + ')'),
            '#66bb6a',
            chipX,
            chipY,
            {
              color:
                jobTypeText === 'aligned'
                  ? intensityColor.low
                  : jobTypeText === 'misaligned'
                    ? intensityColor.high
                    : intensityColor.success,
              outline: true,
              outlineColor:
                jobTypeText === 'aligned'
                  ? intensityColor.low
                  : jobTypeText === 'misaligned'
                    ? intensityColor.high
                    : intensityColor.success,
            },
          );

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Leave at least 100pt space for the section
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin;
          }

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Concept Prevalence
    if (results?.concept_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Concept Prevalence' + ' (' + results?.concept_prevalence?.length + ')', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.concept_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title

        {
          const jobText = frameData.concept;
          const jobTypeText = '';
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Persona Overview
    if (results?.persona_overview) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Persona Overview', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.persona_overview?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(12)
          .setTextColor(40, 40, 40)
          .text(`${frameData.persona_label}`, margin, y + 8);
        pdf
          .setFont('helvetica', 'bold')
          .setFontSize(10)
          .setTextColor(80, 80, 80)
          .text(
            `# Prevalence: ${frameData.prevalence}`,
            pdf.internal.pageSize.width -
              margin -
              pdf.getTextWidth(`# Prevalence: ${frameData.prevalence}`) -
              2,
            y + 8,
          );

        y += 16;

        // Draw dominant context
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);
        const contextLines = pdf.splitTextToSize(
          `Context: ${frameData.dominant_context || 'N/A'}`,
          pdf.internal.pageSize.width - margin * 2,
        );
        contextLines.forEach((line) => {
          pdf.text(line, margin, y + 8);
          y += 16;
        });

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }
        y += 16;

        // Draw representative quotes
        if (frameData.common_motivations?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin + 10;
          }

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Motivations', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_motivations.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('-', margin, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin + 10;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Task Overview
    if (results?.task_overview) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Task Overview', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.task_overview?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        {
          const jobText = frameData.task_label;
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTotalW = chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw representative quotes
        if (frameData.common_friction_points?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin + 10;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Friction Points', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_friction_points.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('-', margin, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
        // Draw common_enablers quotes
        if (frameData.common_enablers?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin + 10;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Enablers', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_enablers.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('-', margin, y + 8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin + 10;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });
        }
      });
    }
    // Start::Job Prevalence
    if (results?.job_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Job Prevalence', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.job_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type + Prevalence Header ---
        {
          const jobText = frameData.job;
          const jobTypeText = frameData.job_type;
          const prevalenceText = `# Prevalence: ${frameData.prevalence}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 4) Draw the jobType as a chip, vertically centered
          // drawChip places text at y + 15, so supply chipY = statsY - 15
          const chipX = pageW - margin - statsTotalW;
          const chipY = statsY - 15;
          drawChip(formatString(jobTypeText), '#66bb6a', chipX, chipY, { color: '#ffffff' });

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw representative quotes
        if (frameData.common_triggers?.length) {
          y += 10; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Triggers', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_triggers.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
        // Draw common_enablers quotes
        if (frameData.common_struggles?.length) {
          if (y > pdf.internal.pageSize.height - 50) {
            pdf.addPage();
            y = margin + 10;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Struggles', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_struggles.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin;
              }
              pdf.text(line, margin, y + 8);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }

        // Draw representative quotes
        if (frameData.common_desired_outcomes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Desired Outcomes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_desired_outcomes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin, y + 8);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::position_prevalence Prevalence
    if (results?.position_prevalence) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Researcher Position Prevalence' + ' ', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.position_prevalence?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type + Prevalence Header ---
        {
          const jobText = frameData.researcher_position;
          const jobTypeText = '';
          const prevalenceText = `# Frequency: ${frameData.frequency}`;

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          const prevalenceW = pdf.getTextWidth(prevalenceText);

          // Compute chip width: text + horizontal padding*2 + extra spacing (8px)
          const chipPaddingH = 12;
          const extraSpacing = 8;
          const chipTextW = pdf.getTextWidth(jobTypeText);
          const chipTotalW = chipTextW + chipPaddingH * 2 + extraSpacing;

          const statsGap = 8; // space between chip and prevalence text
          const statsTotalW = chipTotalW + prevalenceW + statsGap;

          // 2) Wrap the job title to fit remaining space
          pdf.setFont('helvetica', 'bold').setFontSize(12).setTextColor(40, 40, 40);
          const lineHeight = 14;
          const jobMaxW = usableWidth - statsTotalW;
          const jobLines = pdf.splitTextToSize(jobText, jobMaxW);

          const baseY = y + 8;
          jobLines.forEach((line, idx) => {
            pdf.text(line, margin, baseY + idx * lineHeight);
          });

          // 3) Compute baseline Y for chip & prevalence (last line of job)
          const statsY = baseY + (jobLines.length - 1) * lineHeight;

          const pageW = pdf.internal.pageSize.getWidth();

          // 5) Draw the prevalence text, right-aligned
          pdf.setFontSize(10).setTextColor(80, 80, 80);
          const prevalenceX = pageW - margin - prevalenceW;
          pdf.text(prevalenceText, prevalenceX, statsY);

          // 6) Advance y past this entire block
          y += jobLines.length * lineHeight + 12;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw representative quotes
        if (frameData.common_effects?.length) {
          y += 10; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Common Effects', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.common_effects.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }

        // Draw representative quotes
        if (frameData.representative_quotes?.length) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Representative Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.representative_quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 16; // Final space after all quotes
        }
      });
    }
    // Start::Notable Signals
    if (results?.notable_signals) {
      // Check if we have enough space for the section header and some content
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = margin;
      }

      y += 24;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Notable Signals', margin, y);
      y += 20;

      // Draw theme cards with background and separation
      results?.notable_signals?.forEach((frameData) => {
        // Check if we need a new page (leave at least 2 inches at bottom)
        if (y > pdf.internal.pageSize.height - 50) {
          pdf.addPage();
          y = margin;
        }

        // Draw theme title
        // --- Wrapped Job + Job Type + Prevalence Header ---
        {
          const jobText = frameData.moment;
          const jobTextLines = pdf.splitTextToSize(
            jobText,
            pdf.internal.pageSize.width - margin * 2,
          );

          // 1) Measure widths for alignment
          pdf.setFont('helvetica', 'bold').setFontSize(10);
          pdf.setTextColor(60, 60, 60);

          // 5) Draw job text
          jobTextLines.forEach((line, lineIndex) => {
            pdf.text(line, margin, y + lineIndex * 12);
          });

          // 6) Advance y past this entire block
          y += jobTextLines.length * 12 + 10;
        }

        // Check page space before Representative Meaning section
        // Leave at least 100pt space for the section
        if (y > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          y = margin;
        }

        // Draw researcher position
        if (frameData.researcher_position) {
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Researcher Position', margin, y);

          y += 16; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          pdf.text(frameData.researcher_position, margin, y);

          y += 16; // Final space after all quotes
        }
        // Draw researcher position
        if (frameData.effect_on_data) {
          y += 10; // Space before section

          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Effect on Data', margin, y);

          y += 16; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          pdf.text(frameData.effect_on_data, margin, y);

          y += 24; // Final space after all quotes
        }

        // Draw representative quotes
        if (frameData.quotes?.length) {
          if (y > pdf.internal.pageSize.height - 100) {
            pdf.addPage();
            y = margin;
          }
          // Section title with underline
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Quotes', margin, y);

          y += 8; // Space after title

          // Quote styling
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(70, 70, 70);

          frameData.quotes.forEach((quote) => {
            // Check page space before starting a new quote
            if (y > pdf.internal.pageSize.height - 50) {
              pdf.addPage();
              y = margin + 10;
            }

            // Add quote mark
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(200, 200, 200);
            pdf.text('"', margin, y + 8);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(70, 70, 70);

            // Draw quote text
            const quoteLines = pdf.splitTextToSize(
              quote,
              pdf.internal.pageSize.width - 2 * margin - 15, // Account for quote mark
            );

            const lineHeight = 12;
            const quoteTop = y;

            // Draw each line of the quote
            quoteLines.forEach((line) => {
              if (y > pdf.internal.pageSize.height - lineHeight * 2) {
                pdf.addPage();
                y = margin + 10;
              }
              pdf.text(line, margin + 10, y + 10);
              y += lineHeight;
            });

            y += lineHeight; // Add space after each quotes

            // Adjust vertical position after the quote
            y = Math.max(y, quoteTop + quoteLines.length * lineHeight);
          });

          y += 24; // Final space after all quotes
        }
      });
    }

    // Start::Priority Insights
    const priorityInsights =
      results?.priority_insights ||
      results?.priority_actions ||
      results?.priority_recommendations ||
      results?.priority_implications;
    if (priorityInsights) {
      if (y > pdf.internal.pageSize.height - 40) {
        pdf.addPage();
        y = margin;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        results?.priority_insights
          ? 'Priority Insights'
          : results?.priority_actions
            ? 'Priority Actions'
            : results?.priority_recommendations
              ? 'Priority Recommendations'
              : results?.priority_implications
                ? 'Priority Implications'
                : '',
        margin,
        y,
      );
      y += 16;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(70, 70, 70);
      const lineHeight = 18;
      priorityInsights.forEach((action) => {
        const lines = pdf.splitTextToSize(action, 500);
        lines.forEach((line, i) => {
          const newY = y + i * lineHeight;
          if (newY > pdf.internal.pageSize.height - 40) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, margin, y + 7 + i * lineHeight);
        });
        y += lines.length * lineHeight;
      });
      y += 5;
    }

    pdf.save(`${item.name}.pdf`);
  };
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primary" onClick={handleDownloadPdf}>
          Download
        </Button>
      </Box>
      <Card variant="outlined" sx={{ p: 0, mt: 2 }} ref={contentRef}>
        {children}
      </Card>
    </>
  );
};
